import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from "react-dom";
import { TEXTAREA_INPUTWRAPDIV_ID, TEXTAREA_TEXTINPUTDIV_ID, KEY_DELETE } from '@/constants';
import { getElementByQuerySelector, getActiveElement } from '@/helpers';
import { useKeyboardTyping } from '@/hooks';
import {
  Backdrop,
  InputWrapDiv,
  TextInputDiv,
} from './styles';

const EditableSectionContentTextarea = (props) => {
  const {
    text,
    visible,
    targetElementSelector,
    onChange,
    onFocus,
    onBlur,
    sectionAreaSize,
    inputOnType,
    selectedSection,
  } = props;
  const textAreaStyles = {
    fontFamily: sectionAreaSize.fontFamily,
    fontStyle: sectionAreaSize.fontStyle,
    textDecoration: sectionAreaSize.textDecoration,
    fontSize: sectionAreaSize.fontSize,
    fontWeight: sectionAreaSize.fontWeight,
    color: sectionAreaSize.color,
    textAlign: sectionAreaSize.textAlign,
    alignItems: sectionAreaSize.alignItems,
    transform: sectionAreaSize.transform,
    lineHeight: sectionAreaSize.lineHeight,
    overflow: 'hidden',
    whiteSpace: sectionAreaSize.whiteSpace
  };
  const fontWidthFactor = selectedSection.fontWidthFactor
  // "Based on content" sections render single-line (white-space: pre) and resize
  // to their text; fixed-size sections wrap (white-space: normal). Only the former
  // needs the grow-to-content / overflow handling below.
  const isBasedOnContent = sectionAreaSize.whiteSpace === 'pre'
  const [localVisible, setLocalVisible] = useState(false);
  const [isStartedTyping, setStartedTyping] = useState(true);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const inputRef = useRef(null);
  const initVisibleFnRef = useRef();
  const isVisible = visible && localVisible;

  // Keep the leading character visible while typing: the textarea auto-scrolls
  // horizontally to follow the caret (overflow: hidden, white-space: pre) while
  // the based-on-content section width catches up, which would otherwise push
  // the first character past the left border until focus leaves the field.
  //
  // Only pin scrollLeft to 0 while the whole text fits the field. Once the text
  // overflows the textarea (e.g. on browsers without `field-sizing: content`,
  // where the textarea keeps a fixed width), pinning to 0 would scroll the caret
  // / most-recently-typed character off the right edge. In that case leave the
  // browser's caret-following scroll in place so the character being typed stays
  // visible. (+1 px tolerance for sub-pixel rounding.)
  const resetHorizontalScroll = () => {
    const input = inputRef.current;
    if (!input) return;
    if (input.scrollWidth <= input.clientWidth + 1) {
      input.scrollLeft = 0;
    }
  };

  const resizeTextarea = () => {
    setTimeout(() => {
      if (!inputRef.current) return;
      if (!CSS.supports('field-sizing', 'content')) {
        inputRef.current.style.height = 'auto'
        inputRef.current.style.height = `${inputRef.current.scrollHeight}px`
        // No `field-sizing: content` support: for based-on-content sections, grow
        // the width to fit the content too (mirrors the field-sizing path), so the
        // trailing character is not clipped while the section width catches up.
        // Never shrink below the section width kept in `min-width`. Fixed-size
        // sections keep their capped width so the text wraps.
        if (isBasedOnContent) {
          const minWidth = parseFloat(inputRef.current.style.minWidth) || 0;
          inputRef.current.style.width = 'auto'
          inputRef.current.style.width = `${Math.max(inputRef.current.scrollWidth, minWidth)}px`
        }
      }
      resetHorizontalScroll();
    }, 0)
  }

  const handleKeyboardTyping = (event) => {
    if (inputRef.current) {
      if (event.target.innerHTML === 'Apply') {
        setLocalVisible(true);
        inputRef.current.blur();
        onBlur();
      } else {
        if (getActiveElement()?.nodeName === 'INPUT') return; // this block is necessary
        else initVisibleFnRef.current();
      }
    }
  }

  useKeyboardTyping({ handleKeyboardTyping });

  useEffect(() => {
    return () => {
      onBlur();
    }
  }, []);

  useEffect(() => {
    if (visible) {
      initVisibleFnRef.current();
      resizeTextarea();
    }
  }, [visible]);

  // will always create new instance of function with latest context. need for useEffect of 'keydown' subscriptions
  // need to use latest correct targetElementSelector value
  initVisibleFnRef.current = () => {
    // set text like this to fix wrong behavior with input cursor
    inputRef.current.value = text;
    inputRef.current.focus({ preventScroll: true });
    resetHorizontalScroll();
    // FIXME: wrong position for TM materials(ex. TC_US-TM-100.mat.json)
    const targetElement = getElementByQuerySelector(targetElementSelector);
    if (targetElement) {
      const boundRect = targetElement.getBoundingClientRect();
      let transformStr = '';
      let top = boundRect.top;
      let left = boundRect.left;
      let height = boundRect.height;
      let width = boundRect.width;

      if (sectionAreaSize?.angle) {
        transformStr = `${transformStr} rotate(${sectionAreaSize.angle}deg)`;
        if (sectionAreaSize.angle === 90) {
          width = boundRect.height;
          height = boundRect.width;
          top = top + (width - height) / 2;
          left = left - (width - height) / 2;
        } else if (sectionAreaSize.angle === 270) {
          width = boundRect.height;
          height = boundRect.width;
          top = top - (width - height) / 2;
          left = left + (width - height) / 2;
        }
      }
      if (sectionAreaSize?.mirrorX) {
        transformStr = `${transformStr} scale(-1, 1)`
      }
      if (sectionAreaSize?.mirrorY) {
        transformStr = `${transformStr} scale(1, -1)`
      }
      setPosition({
        top,
        left,
        maxHeight: height,
        maxWidth: width,
        height,
        width,
        justifyContent: sectionAreaSize.textAlign,
        alignItems: sectionAreaSize.alignItems,
        transform: transformStr,
      });
    }

    setLocalVisible(true);
    onFocus();
  };

  const onBackdropClick = (e) => {
    setStartedTyping(true);
    setLocalVisible(false);
    inputRef.current.blur();
  };

  const handleChange = (e) => {
    if (isStartedTyping) {
      inputOnType(e.target.value);
      setStartedTyping(false);
    }
    onChange(e.target.value);
    resizeTextarea();
    resetHorizontalScroll();
  };

  const handleBlur = () => {
    onBlur();
    onBackdropClick();
  }

  const handleClick = (e) => {
    e.stopPropagation();
  }

  const handleKeyDown = (event) => {
    if (event.key === KEY_DELETE) {
      event.preventDefault();

      const input = inputRef.current;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const value = input.value;

      // Handle selection or remove character after the cursor
      if (start === end) {
        // No selection, delete the character after the cursor
        if (start < value.length) {
          input.value = value.slice(0, start) + value.slice(start + 1);
          input.setSelectionRange(start, start);

          inputOnType(input.value);
        }
      } else {
        // If there is a selection, delete the selected text
        input.value = value.slice(0, start) + value.slice(end);
        input.setSelectionRange(start, start);

        inputOnType(input.value);
      }
    }
  };

  const cappedWidth = `${position.width / fontWidthFactor}px`;
  const sizeTransform = {
    transform: `scaleX(${fontWidthFactor})`,
    // Based-on-content (single-line, white-space: pre): let the textarea grow to
    // fit its content via `field-sizing: content` instead of being hard-capped at
    // the section width. The section width trails the typed content by one
    // keystroke, so a hard cap would clip either the leading character (scrollLeft
    // pinned to 0) or the most-recently-typed one (caret-following scroll).
    // Keeping at least the section width here, and letting the wrapper overflow
    // (see `$allowOverflow` in styles.js), keeps both ends visible while typing.
    // Fixed-size sections (white-space: normal) keep the hard cap so text wraps
    // within the section instead of expanding onto a single line.
    ...(isBasedOnContent
      ? { minWidth: cappedWidth, width: 'auto' }
      : { width: cappedWidth }),
    fieldSizing: 'content'
  }
  // TODO: refactoring: exclude portals to another component for better performance??
  return ReactDOM.createPortal(
    <Backdrop $visible={isVisible}>
      <InputWrapDiv
        id={TEXTAREA_INPUTWRAPDIV_ID}
        $position={position}
        $justifyContent={sectionAreaSize.textAlign}
        $alignItems={sectionAreaSize.alignItems}
        $allowOverflow={isBasedOnContent}
      >
        <TextInputDiv
          id={TEXTAREA_TEXTINPUTDIV_ID}
          ref={inputRef}
          style={{ ...textAreaStyles, ...sizeTransform }}
          placeholder="."
          rows={1}
          $fontName="Lato"
          onChange={handleChange}
          onClick={handleClick}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      </InputWrapDiv>
    </Backdrop>,
    getElementByQuerySelector('#floatable-section-content-textarea-portal'),
  );
};

export default React.memo(EditableSectionContentTextarea);